/**
 * Offline Outbox Service
 * Manages a queue of operations to be synced when network is available
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from './logger';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';

export type OutboxOperationType = 'settings' | 'profile' | 'quiz_result' | 'ad_create' | 'report';

export interface OutboxOperation {
  id: string;
  type: OutboxOperationType;
  payload: Record<string, unknown>;
  createdAt: string;
  retries: number;
  maxRetries: number;
}

const OUTBOX_KEY = 'drug_offline_outbox';
const MAX_RETRIES = 3;

/**
 * Generate unique ID for outbox operation
 */
function generateId(): string {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get all operations from outbox
 */
export function getOutbox(): OutboxOperation[] {
  try {
    const data = localStorage.getItem(OUTBOX_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

/**
 * Save outbox to localStorage
 */
function saveOutbox(operations: OutboxOperation[]): void {
  try {
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(operations));
  } catch (e) {
    logger.error('Failed to save outbox', { data: { error: String(e) } });
  }
}

/**
 * Add operation to outbox
 */
export function addToOutbox(
  type: OutboxOperationType,
  payload: Record<string, unknown>
): string {
  const operations = getOutbox();
  const id = generateId();
  
  const operation: OutboxOperation = {
    id,
    type,
    payload,
    createdAt: new Date().toISOString(),
    retries: 0,
    maxRetries: MAX_RETRIES,
  };
  
  operations.push(operation);
  saveOutbox(operations);
  
  logger.info('Added to offline outbox', { data: { outboxType: type, outboxId: id } });
  
  // Dispatch event for UI updates
  window.dispatchEvent(new CustomEvent('outbox-updated', { detail: { count: operations.length } }));
  
  return id;
}

/**
 * Remove operation from outbox
 */
export function removeFromOutbox(id: string): void {
  const operations = getOutbox().filter(op => op.id !== id);
  saveOutbox(operations);
  
  // Dispatch event for UI updates
  window.dispatchEvent(new CustomEvent('outbox-updated', { detail: { count: operations.length } }));
}

/**
 * Get count of pending operations
 */
export function getOutboxCount(): number {
  return getOutbox().length;
}

/**
 * Check if outbox has pending operations
 */
export function hasOutboxOperations(): boolean {
  return getOutboxCount() > 0;
}

/**
 * Process a single operation
 */
async function processOperation(operation: OutboxOperation): Promise<boolean> {
  try {
    switch (operation.type) {
      case 'settings': {
        const { key, value, userId } = operation.payload as { key: string; value: unknown; userId: string };
        
        // Try to update ads_consent in DB
        if (key === 'marketingConsent' && userId) {
          const { error } = await supabase
            .from('users')
            .update({ ads_consent: value as boolean })
            .eq('id', userId);
          
          if (error) throw error;
        }
        return true;
      }
      
      case 'profile': {
        const { userId, updates } = operation.payload as { userId: string; updates: Record<string, unknown> };
        
        const { error } = await supabase
          .from('users')
          .update(updates)
          .eq('id', userId);
        
        if (error) throw error;
        return true;
      }
      
      case 'quiz_result': {
        const { userId, formResponseId, answersJson, templateId } = operation.payload as {
          userId: string;
          formResponseId?: string;
          answersJson: Json;
          templateId: string;
        };
        
        if (formResponseId) {
          // Update existing response
          const { error } = await supabase
            .from('form_responses')
            .update({
              answers_json: answersJson,
              completed: true,
              updated_at: new Date().toISOString(),
            })
            .eq('id', formResponseId);
          
          if (error) throw error;
        } else {
          // Create new response
          const { error } = await supabase
            .from('form_responses')
            .insert({
              submitted_by_user_id: userId,
              owner_entity_id: userId,
              owner_entity_type: 'user',
              template_id: templateId,
              answers_json: answersJson,
              completed: true,
            });
          
          if (error) throw error;
        }
        return true;
      }
      
      case 'report': {
        const { entityId, entityType, reason, reporterId } = operation.payload as {
          entityId: string;
          entityType: string;
          reason: string;
          reporterId: string;
        };
        
        // Create moderation check
        const { error } = await supabase
          .from('moderation_checks')
          .insert({
            entity_id: entityId,
            entity_type: entityType,
            source: 'manual',
            source_codifier_key: 'moderation_source',
            decision: 'needs_review',
            decision_codifier_key: 'moderation_decision',
            reason: reason,
          });
        
        if (error) throw error;
        
        // Log activity event
        await supabase.from('activity_events').insert({
          event_type: 'user_report_submitted',
          event_type_codifier_key: 'activity_event_type',
          actor_user_id: reporterId,
          entity_type: entityType,
          entity_id: entityId,
          payload_json: { reason } as Json,
        });
        
        return true;
      }
      
      default:
        logger.warn('Unknown outbox operation type', { data: { operationType: operation.type } });
        return true; // Remove unknown operations
    }
  } catch (error) {
    logger.error('Failed to process outbox operation', { 
      data: { 
        operationType: operation.type, 
        operationId: operation.id, 
        error: String(error) 
      }
    });
    return false;
  }
}

/**
 * Sync all pending operations
 */
export async function syncOutbox(): Promise<{ success: number; failed: number }> {
  if (!navigator.onLine) {
    return { success: 0, failed: 0 };
  }
  
  const operations = getOutbox();
  if (operations.length === 0) {
    return { success: 0, failed: 0 };
  }
  
  logger.info('Syncing offline outbox', { data: { operationsCount: operations.length } });
  
  let success = 0;
  let failed = 0;
  const remainingOperations: OutboxOperation[] = [];
  
  for (const operation of operations) {
    const result = await processOperation(operation);
    
    if (result) {
      success++;
    } else {
      operation.retries++;
      
      if (operation.retries < operation.maxRetries) {
        remainingOperations.push(operation);
      } else {
        failed++;
        logger.error('Outbox operation exceeded max retries', { 
          data: { operationId: operation.id, operationType: operation.type } 
        });
      }
    }
  }
  
  saveOutbox(remainingOperations);
  
  // Dispatch event for UI updates
  window.dispatchEvent(new CustomEvent('outbox-updated', { detail: { count: remainingOperations.length } }));
  
  if (success > 0) {
    toast.success(`Синхронизировано: ${success} ${success === 1 ? 'операция' : 'операций'}`);
  }
  
  if (failed > 0) {
    toast.error(`Не удалось синхронизировать: ${failed}`);
  }
  
  return { success, failed };
}

/**
 * Initialize outbox sync listener
 * Call this once at app startup
 */
export function initOutboxSync(): () => void {
  const handleOnline = () => {
    // Small delay to ensure connection is stable
    setTimeout(() => {
      syncOutbox();
    }, 1000);
  };
  
  window.addEventListener('network-online', handleOnline);
  
  // Also try to sync on page load if online
  if (navigator.onLine && hasOutboxOperations()) {
    setTimeout(() => {
      syncOutbox();
    }, 2000);
  }
  
  return () => {
    window.removeEventListener('network-online', handleOnline);
  };
}
